import { ENTER } from '@angular/cdk/keycodes';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  inject,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  fromEvent,
  map,
  Observable,
  switchMap,
  takeUntil,
} from 'rxjs';

import { TagColorPalette } from '@blaze-runtime/engine-domain';
import {
  CanvasElementDataProperties,
  CanvasElementPropertiesWithData,
  DataBindingSourceInfo,
  getActiveBinding,
  getBackground,
  InputElementMetaUiFeatures,
  OptionPickerDefaultValue,
  PropertiesConfigInputColorPaletteModel,
} from '@blaze-shared/elements-domain';
import { DatasetRow, selectSubsetByValue } from '@blaze-shared/schema-domain';
import { safeDetectChanges } from '@blaze-shared/shared-ui';
import {
  isDeepEqual,
  isNullish,
  setAlphaToHexColor,
  ValueWithUnitOfMeasure,
} from '@blaze-shared/shared-util';
import { CssMediaService } from '@blaze-shared/theming-data-access';
import { FALLBACK_THEME, makeThemeValueString } from '@blaze-shared/theming-domain';

import { CanvasElementComponent } from '../../canvas-elements/element-component';
import { arePropsWithData } from '../../canvas-elements/element-model/element-props-guards';
import {
  filterOptionsWithEmptyLabel,
  isListSourceBound,
  serializeBindingOutput,
} from '../../utils/canvas-element/bindings.utils';
import { CanvasElementBaseInputSelectMultipleComponent } from '../__base/base-input-select-multi.component';

@UntilDestroy()
@Component({
  selector: 'be-tags',
  templateUrl: './tags.component.html',
  styleUrls: ['./tags.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TagsComponent
  extends CanvasElementBaseInputSelectMultipleComponent<'tags'>
  implements CanvasElementComponent<'tags'>, OnInit, OnChanges, AfterViewInit, OnDestroy
{
  private readonly cssMediaService = inject(CssMediaService);

  @ViewChild('inputContainer') inputContainer!: ElementRef<HTMLDivElement>;
  @ViewChild(MatMenuTrigger) menuTrigger!: MatMenuTrigger;
  @ViewChild('filterInput') filterInput?: ElementRef<HTMLInputElement>;
  @ViewChild(MatMenu) menuContainer!: MatMenu;

  @HostBinding('style.--b-te-accent-color') accentColor = FALLBACK_THEME.value.accent;

  private readonly dataSource$$ = new BehaviorSubject<DataBindingSourceInfo | undefined>(undefined);
  private readonly tagColorPalette$: Observable<TagColorPalette | undefined>;

  readonly separatorKeysCodes = [ENTER];

  displayedOptions: DatasetRow[] = [];

  readonly elementKeyRoot = 'b-te-dd';
  readonly elementSizeKeyRoot = 'b-te-s';
  elementKey!: string;
  elementSizeKey!: string;

  labelEnabled!: boolean;
  labelText!: string;
  defaultValue: OptionPickerDefaultValue = 'none';
  placeholder!: string;

  styles!: Record<string, string>;
  inputWrapperStyles!: Record<string, string>;
  validationStyles!: Record<string, string>;
  themeAccentColor!: string;
  fontSize!: ValueWithUnitOfMeasure | undefined;
  fontFamily?: string;
  lineHeight!: ValueWithUnitOfMeasure | undefined;
  inputPaddings = 16; // 2* 8px padding
  tagColorPalette: TagColorPalette | undefined;

  isReadOnly = false;
  itemInList = false;
  createdTags: Record<string, string>[] = [];
  isTouchMode = this.cssMediaService.isTouchMode;

  get hasValidationError(): boolean {
    return !!this.formState?.errors?.required || !!this.formState?.errors?.invalid;
  }

  get isMenuOpen(): boolean {
    return !!this.menuTrigger?.menuOpen;
  }

  get canAddNewItems(): boolean {
    return this.dataOptionsList.column?.tagDefinition?.allowDynamicValues ?? false;
  }

  get tagDefaultPalette():
    | Pick<PropertiesConfigInputColorPaletteModel, 'foreground' | 'background'>
    | undefined {
    return this.props?.style.tags?.palette;
  }

  constructor() {
    super();

    this.tagColorPalette$ = combineLatest({
      props: this.props$.pipe(
        map((props) => ({
          style: { tags: { palette: props?.style?.tags?.palette } },
          data: props?.data,
        })),
        distinctUntilChanged(isDeepEqual),
      ),
      schema: this.createPaletteSchemaObservable(this.dataSource$$, 'optionsList'),
      dataOptionsList: this.getOptionsListSource(),
    }).pipe(
      map(({ schema, props, dataOptionsList }) => {
        const tagsPalette = props?.style?.tags?.palette;
        const values = dataOptionsList?.map((v) => v[this.valueField]);

        if (!schema || !arePropsWithData(props)) {
          return this.elementTagsService.mapValuesToColorPalette(
            values,
            undefined,
            undefined,
            tagsPalette,
          );
        }

        const binding = props.data?.find((b) => b.key === 'optionsList');
        return this.elementTagsService.mapValuesToColorPalette(
          values,
          binding?.dataSource?.name,
          schema,
          tagsPalette,
        );
      }),
    );

    this.initMarginHandler((props) => props?.style?.general?.offset?.margin);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['props']) {
      if (!this.elementKey) {
        this.elementKey = `${this.elementKeyRoot}-${this.elementId}`;
        this.elementSizeKey = `${this.elementSizeKeyRoot}-${this.elementId}`;
      }

      const { tags, title, options } = this.props?.content || {};

      this.labelEnabled = title?.enabled ?? true;
      this.itemInList = false;
      this.isSingleSelect = options?.singleSelect || false;
      this.placeholder = tags?.placeholder || '';

      this.processBindingOptionsField(tags?.values);
      this.updateStyles();
    }

    if (changes?.['props'] || changes?.['selected'] || changes?.['optionsList']) {
      const optionsList = (this.props as CanvasElementPropertiesWithData)?.data?.find(
        (d) => d.key === 'optionsList',
      );
      const dataSource = optionsList?.dataSource;
      this.dataSource$$.next(optionsList?.enabled ? dataSource : undefined);
      this.updateSelection();
    }

    if (changes?.['props']) {
      this.isSourceBound = isListSourceBound(this.props?.data as CanvasElementDataProperties[]);
    }
  }

  ngOnInit(): void {
    this.getElementUiFeatures<InputElementMetaUiFeatures>()
      .pipe(untilDestroyed(this))
      .subscribe((features) => {
        this.labelText = (features.title ?? '') + this.getRequiredSuffix(features);

        this.isReadOnly = features.isReadOnly || this.props?.content?.options?.readonly || false;

        if (this.isMenuOpen && this.isReadOnly) {
          this.menuTrigger?.closeMenu();
        }

        this.updateStyles();
        safeDetectChanges(this.cdr);
      });

    this.stylesCleanup.register([this.elementKey, this.elementSizeKey]);
    this.tagColorPalette$.pipe(untilDestroyed(this)).subscribe((palette) => {
      this.tagColorPalette = palette;
      this.cdr.detectChanges();
    });
  }

  ngAfterViewInit(): void {
    /**
     * Handle clicks outside element / menu
     */
    this.menuTrigger.menuOpened
      .pipe(
        switchMap(() =>
          fromEvent(document.documentElement, 'click').pipe(takeUntil(this.menuTrigger.menuClosed)),
        ),
        untilDestroyed(this),
      )
      .subscribe((event) => {
        const panelElement = document.getElementById(this.menuContainer.panelId);
        const target = event.target as HTMLElement;
        if (this.inputContainer.nativeElement.contains(target) || panelElement?.contains(target)) {
          return;
        }
        this.menuTrigger.closeMenu();
      });

    /**
     * Clear filter input when closing dropdown
     */
    this.menuTrigger.menuClosed.pipe(untilDestroyed(this)).subscribe(() => {
      if (this.filterInput) {
        this.filterInput.nativeElement.value = '';
      }
      this.filterOptions();
      this.cdr.detectChanges();
    });
  }

  ngOnDestroy(): void {
    this.stylesCleanup.dispose([this.elementKey, this.elementSizeKey]);
  }

  onResized(box: DOMRectReadOnly): void {
    this.stylesCleanup.update(
      `${this.elementSizeKey}`,
      `.${this.elementKey} { width: ${box.width + this.inputPaddings}px; }`,
    );
  }

  onInputInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.filterOptions(value);
  }

  onInputContainerClick(event: MouseEvent): void {
    if (this.isReadOnly) {
      return;
    }

    const target = event.target as HTMLElement;

    if (!this.isMenuOpen) {
      this.menuTrigger.openMenu();
      this.focusFilterInput();
    } else if (target !== this.filterInput?.nativeElement) {
      this.menuTrigger.closeMenu();
    }
  }

  onInputKeyDown(event: KeyboardEvent): void {
    // BL-8287 - Temporary solution
    // Pass through only specific events so the FocusKeyManager does not handle every keydown event
    // and take focus, blurring the input in effect.
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      return;
    }

    event.stopPropagation();
  }

  onInputKeyUp(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      this.menuContainer.focusFirstItem();
      return;
    }

    if (event.code === 'Enter') {
      this.handleTagSelection(event);
    }
    this.updateItemInList();
  }

  onCreateTagClick(): void {
    this.createTag();
    this.handleTagSelection();
    this.updateItemInList();
  }

  selectTag(item: DatasetRow, event?: Event): void {
    this.selection.select(item);
    if (this.filterInput) {
      this.filterInput.nativeElement.value = '';
    }
    this.filterOptions();
    this.focusFilterInput();
    if (!this.isSingleSelect && this.displayedOptions.length > 0) {
      event?.stopPropagation();
    }
  }

  createTag(): void {
    if (!this.filterInput) {
      return;
    }
    const newVal = this.filterInput.nativeElement.value;
    if (
      (!this.canAddNewItems && !this.isReadOnly) ||
      this.createdTags.find((el) => el.value === newVal) ||
      this.optionsList?.find((el) => el[this.valueField] === newVal)
    ) {
      return;
    }

    this.createdTags.push({ [this.valueField]: newVal, label: newVal, value: newVal });
  }

  removeTag(item: DatasetRow): void {
    if (!this.isReadOnly) {
      this.selection.deselect(item);
    }
  }

  focusFilterInput(): void {
    setTimeout(() => {
      if (!this.filterInput) {
        return;
      }
      this.filterInput.nativeElement.focus();
    }, 0);
  }

  reset(): void {
    this.outputEmitTracker.reset();
    this.updateSelection();
    this.cdr.detectChanges();
  }

  protected override filterOptions(value = ''): void {
    const needle = value.trim().toLowerCase();
    const options =
      [...(this.optionsList || []), ...this.createdTags]
        .filter(Boolean)
        // Filter fields that have no label assigned to it
        ?.filter(filterOptionsWithEmptyLabel(this.labelField))
        // And those that are selected already
        .filter(
          (option) =>
            !this.selection.selected.some(
              (selected) => selected[this.valueField] === option[this.valueField],
            ),
        ) || [];

    this.displayedOptions = value
      ? options.filter((option) =>
          [this.labelField, this.valueField].some((key) => {
            const haystack = option[key].toString().toLowerCase();
            return haystack.includes(needle);
          }),
        )
      : options;
  }

  protected override updateOutput(data?: any[] | null): void {
    data ??= [];
    this.updateSelectionModel(data);

    let serialized: any = serializeBindingOutput(
      this.props,
      data,
      this.valueField,
      this.labelField,
    );
    if (this.isSingleSelect) {
      serialized = serialized[0] ?? null;
    }

    this.selected = serialized;
    this.selectedValue = serialized;
    const isInitialData = !this.outputEmitTracker.hasPrevious();

    if (!this.outputEmitTracker.shouldEmit(serialized)) {
      return;
    }

    this.output.emit({
      eventType: 'change',
      data: serialized,
      isInitialData,
      schema: this.getEventSchema('change'),
    });
  }

  private handleTagSelection(event?: KeyboardEvent): void {
    if (!this.filterInput) {
      return;
    }
    const value = this.filterInput.nativeElement.value.trim();

    if (value && (this.displayedOptions.length || this.canAddNewItems)) {
      this.selectTag(
        this.displayedOptions[0] ?? this.createdTags.find((el) => el.value === value),
        event,
      );
      this.menuTrigger.closeMenu();
    }
  }

  private updateItemInList(): void {
    if (!this.filterInput) {
      return;
    }
    const value = this.filterInput.nativeElement.value.trim();
    this.itemInList = !!this.displayedOptions.find((el) => el[this.valueField] === value);
  }

  private updateSelection(): void {
    const { tags, data } = this.props?.content || {};

    const optionsList = [this.optionsList || [], this.createdTags].flat();
    const wasSourceBound = this.isSourceBound;
    const isSourceBound = isListSourceBound(this.props?.data as CanvasElementDataProperties[]);
    if (wasSourceBound !== undefined && wasSourceBound !== isSourceBound) {
      this.selectedValue = [];
    }
    this.defaultValue = data?.defaultValue || 'none';
    this.filterOptions();

    let selected = null;
    if (
      !isNullish(this.selectedValue) &&
      (!Array.isArray(this.selectedValue) || this.selectedValue?.length)
    ) {
      const selection = Array.isArray(this.selectedValue)
        ? this.selectedValue
        : [this.selectedValue];
      selected = selectSubsetByValue(optionsList, selection, this.valueField);
    } else {
      switch (this.defaultValue) {
        case 'fixedValue':
          if (tags?.preselect) {
            selected = selectSubsetByValue(optionsList, tags?.preselect, this.valueField);
          }
          break;
      }
    }

    // Update the position after the options get updated to prevent empty space
    // between the input and the menu in some cases
    setTimeout(() => {
      this.menuTrigger?.updatePosition();
    });

    if (optionsList.length) {
      this.updateOutput(selected);
    }
  }

  private updateStyles(): void {
    const { style, content } = this.props || {};
    const { title } = content || {};
    const { general, input, list, labelBlock } = style || {};
    if (!style || !title || !general || !input || !list || !labelBlock) {
      return;
    }
    this.fontSize = style.fontSize;
    this.fontFamily = style.fontFamily;
    this.lineHeight = style.lineHeight;
    this.accentColor = general.accent.color1;

    const accent5 = setAlphaToHexColor(general.accent.color1, 5);

    const styles: Record<string, string> = {};
    const inputWrapperStyles: Record<string, string> = {
      background: getBackground(input.fill),
      color: input.color.color1,
    };
    const validationStyles: Record<string, string> = {};

    if (title.position === 'top') {
      styles['flex-direction'] = `column`;
      if (content?.options?.readonly) {
        inputWrapperStyles['padding-left'] = '0';
      }
    } else {
      validationStyles['margin-left'] = title.width;
      validationStyles['padding-left'] = '8px';
      inputWrapperStyles['flex-basis'] = `${100 - parseInt(title.width, 10)}%`;
    }

    if (input.border.selectorType !== 'none') {
      inputWrapperStyles['border'] = `1px solid ${input.border.color1}`;
      this.inputPaddings = 18;
    }

    if (input.radius !== '0px') {
      inputWrapperStyles['border-radius'] = input.radius;
    }

    this.themeAccentColor = getComputedStyle(document.body).getPropertyValue(
      makeThemeValueString('accent'),
    );

    this.styles = styles;
    this.inputWrapperStyles = inputWrapperStyles;
    this.validationStyles = validationStyles;

    this.stylesCleanup.update(
      `${this.elementKey}`,
      `
    .${this.elementKey} {
      background: ${getBackground(list.background)};
      ${list.border.selectorType !== 'none' ? `border: 1px solid ${list.border.color1};` : ''}
      border-radius: ${list.radius};
    }
    .${this.elementKey} .mat-mdc-menu-item {
      color: ${list.value.color1};
      font-size: ${style.fontSize};
      font-family: ${style.fontFamily};
      line-height: ${style.lineHeight};
      border-radius: ${list.radius};
    }
    .${this.elementKey} button:hover {
      color: ${general.accent.color1};
    }
    .${this.elementKey} .active {
      background: ${accent5};
      color: ${general.accent.color1};
    }`,
    );
  }

  /**
   * Gets a source for possible tags to be selected.
   * Used for palette generation.
   * When no optionsList is bound, values from props are being used.
   * @private
   */
  private getOptionsListSource(): Observable<DatasetRow[]> {
    return this.props$.pipe(
      map((props) => !!getActiveBinding(props?.data, 'optionsList')),
      distinctUntilChanged(),
      switchMap((isBound) =>
        isBound
          ? (this.dataOptionsList.data$ as any as Observable<DatasetRow[]>)
          : this.props$.pipe(
              map((props) => props?.content.tags.values ?? []),
              distinctUntilChanged(isDeepEqual),
              map((values) => {
                return values.map((value) => ({
                  [this.labelField]: value,
                  [this.valueField]: value,
                }));
              }),
            ),
      ),
    );
  }
}
